import {
  IBase,
  IBasicServiceInfo,
  IVehicleSchedule,
  IVehicleScheduleAvailability,
  IVehicleScheduleChange,
  IVehicleScheduleEdit,
  IVehicleScheduleTrip,
  IVehicleScheduleUpdate,
} from 'src/app/models';
import { ScheduleGroupBy } from 'src/app/models/enums';

type ScheduleSlot = Pick<IVehicleScheduleTrip, 'startDateTime' | 'endDateTime' | 'driver' | 'vehicle'>;

export function withTime(date: Date, time: string): Date {
  const [hours, minutes] = time.split(':').map(Number);
  const updated = new Date(date);
  updated.setHours(hours, minutes, 0, 0);
  return updated;
}

function editedDate(date: Date, time: string, shiftMinutes?: number): Date {
  return shiftMinutes === undefined ? withTime(date, time) : new Date(date.getTime() + shiftMinutes * 60000);
}

function editedSlot(slot: ScheduleSlot, edit: IVehicleScheduleEdit): ScheduleSlot {
  return {
    driver: edit.driver,
    vehicle: edit.vehicle,
    startDateTime: editedDate(slot.startDateTime, edit.startTime, edit.shiftMinutes),
    endDateTime: editedDate(slot.endDateTime, edit.endTime, edit.shiftMinutes),
  };
}

export function sameSlot(a: ScheduleSlot, b: ScheduleSlot): boolean {
  return (
    a.driver?.id === b.driver?.id &&
    a.vehicle?.id === b.vehicle?.id &&
    a.startDateTime.getTime() === b.startDateTime.getTime() &&
    a.endDateTime.getTime() === b.endDateTime.getTime()
  );
}

export function shiftTrip(trip: IVehicleScheduleTrip, shiftMs: number): IVehicleScheduleTrip {
  return {
    ...trip,
    startDateTime: new Date(trip.startDateTime.getTime() + shiftMs),
    endDateTime: new Date(trip.endDateTime.getTime() + shiftMs),
  };
}

function reassignedFields(availability: IVehicleScheduleAvailability, edit: IVehicleScheduleEdit): Pick<IVehicleScheduleTrip, 'driver' | 'vehicle'> {
  const fields: Pick<IVehicleScheduleTrip, 'driver' | 'vehicle'> = {};
  if (edit.driver?.id !== availability.driver?.id) {
    fields.driver = edit.driver;
  }
  if (edit.vehicle?.id !== availability.vehicle?.id) {
    fields.vehicle = edit.vehicle;
  }
  return fields;
}

export function editedAvailabilitySlot(availability: IVehicleScheduleAvailability, edit: IVehicleScheduleEdit): IVehicleScheduleAvailability {
  const slot = editedSlot(availability, edit);
  const edited = { ...availability, ...slot };
  const shiftMs = edited.startDateTime.getTime() - availability.startDateTime.getTime();
  const reassigned = edit.applyToTrips ? reassignedFields(availability, edit) : {};
  if (!shiftMs && !Object.keys(reassigned).length) {
    return edited;
  }
  return { ...edited, trips: availability.trips.map((trip) => ({ ...shiftTrip(trip, shiftMs), ...reassigned })) };
}

function withTripIds(tripIds: number[], trips: IVehicleScheduleTrip[]): number[] {
  const added = trips.map((trip) => trip.id).filter((id): id is number => id !== undefined && !tripIds.includes(id));
  return added.length ? [...tripIds, ...added] : tripIds;
}

export function recordScheduleEdit(
  availability: IVehicleScheduleAvailability,
  edit: IVehicleScheduleEdit,
  previous?: IVehicleScheduleChange,
): IVehicleScheduleChange | undefined {
  const trip = availability.trips.find((item) => edit.tripId !== undefined && item.id === edit.tripId);
  const target: ScheduleSlot = trip ?? availability;
  const edited = editedSlot(target, edit);
  if (sameSlot(target, edited)) {
    return previous;
  }
  const changedTripIds = previous?.changedTripIds ?? [];
  if (trip) {
    return {
      availability: { ...availability, trips: availability.trips.map((item) => (item === trip ? { ...item, ...edited } : item)) },
      availabilityChanged: previous?.availabilityChanged ?? false,
      changedTripIds: withTripIds(changedTripIds, [trip]),
    };
  }
  const editedAvailability = editedAvailabilitySlot(availability, edit);
  return {
    availability: editedAvailability,
    availabilityChanged: true,
    changedTripIds: editedAvailability.trips === availability.trips ? changedTripIds : withTripIds(changedTripIds, availability.trips),
  };
}

export function editedTripSlot(trip: IVehicleScheduleTrip, edit: IVehicleScheduleEdit): IVehicleScheduleTrip {
  return { ...trip, ...editedSlot(trip, edit) };
}

export function sameRoute(availability: IVehicleScheduleAvailability, trip: IVehicleScheduleTrip): boolean {
  return !availability.route || !trip.route || availability.route.id === trip.route.id;
}

export function fitsAvailability(availability: IVehicleScheduleAvailability, trip: IVehicleScheduleTrip): boolean {
  return availability.startDateTime <= trip.startDateTime && trip.endDateTime <= availability.endDateTime;
}

export function recordTripMove(
  source: IVehicleScheduleAvailability,
  target: IVehicleScheduleAvailability,
  edit: IVehicleScheduleEdit,
  sourcePrevious?: IVehicleScheduleChange,
  targetPrevious?: IVehicleScheduleChange,
): IVehicleScheduleChange[] {
  const trip = source.trips.find((item) => item.id === edit.tripId);
  if (!trip) {
    return [];
  }
  return [
    {
      availability: { ...source, trips: source.trips.filter((item) => item !== trip) },
      availabilityChanged: sourcePrevious?.availabilityChanged ?? false,
      changedTripIds: (sourcePrevious?.changedTripIds ?? []).filter((id) => id !== trip.id),
    },
    {
      availability: { ...target, trips: [...target.trips, editedTripSlot(trip, edit)] },
      availabilityChanged: targetPrevious?.availabilityChanged ?? false,
      changedTripIds: withTripIds(targetPrevious?.changedTripIds ?? [], [trip]),
    },
  ];
}

export function mergeAvailabilityFragments(fragments: IVehicleScheduleAvailability[]): IVehicleScheduleAvailability | undefined {
  if (!fragments.length) {
    return undefined;
  }
  const trips = fragments.flatMap((fragment) => fragment.trips);
  return { ...fragments[0], trips: trips.filter((trip, index) => trips.findIndex((item) => item.id === trip.id) === index) };
}

export function scheduleServiceLabel(service?: IBasicServiceInfo): string {
  if (!service) return '';
  return service.name ? `${service.tag}: ${service.name}` : service.tag;
}

export function scheduleUpdateRequest(
  changes: IVehicleScheduleChange[],
  sendNotification: boolean,
): IVehicleScheduleUpdate {
  return {
    sendNotification,
    availabilities: changes.map(({ availability, availabilityChanged, changedTripIds }) => ({
      id: availability.id,
      startDateTime: availabilityChanged ? availability.startDateTime : undefined,
      endDateTime: availabilityChanged ? availability.endDateTime : undefined,
      driverId: availability.driver?.id,
      vehicleId: availability.vehicle?.id,
      trips: availability.trips
        .filter((trip) => trip.id !== undefined && changedTripIds.includes(trip.id))
        .map((trip) => ({
          id: trip.id!,
          startDateTime: trip.startDateTime,
          driverId: trip.driver?.id,
          vehicleId: trip.vehicle?.id,
        })),
    })),
  };
}

export function scheduleRowField(groupBy: ScheduleGroupBy): 'driver' | 'vehicle' {
  return groupBy === ScheduleGroupBy.DRIVERS ? 'driver' : 'vehicle';
}

export function movedTo(loaded?: IBase, edited?: IBase): IBase | undefined {
  return edited?.id !== undefined && edited.id !== loaded?.id ? edited : undefined;
}

export function placeScheduleChanges(
  schedules: IVehicleSchedule[],
  changes: Map<number, IVehicleScheduleChange>,
  groupBy: ScheduleGroupBy,
): IVehicleSchedule[] {
  if (!changes.size) {
    return schedules;
  }
  const field = scheduleRowField(groupBy);
  const rows = new Map(schedules.map((schedule) => [schedule.id, { ...schedule, availabilities: [] as IVehicleScheduleAvailability[] }]));
  const place = (row: IBase, availability: IVehicleScheduleAvailability) => {
    const target = rows.get(row.id) ?? { id: row.id, name: row.name, active: true, availabilities: [] };
    rows.set(row.id, target);
    const index = target.availabilities.findIndex((item) => item.id === availability.id);
    if (index < 0) {
      target.availabilities.push(availability);
    } else {
      const existing = target.availabilities[index];
      target.availabilities[index] = { ...existing, trips: [...existing.trips, ...availability.trips] };
    }
  };

  const loadedTripIds = new Map<number, Set<number | undefined>>();
  const loadedRows = new Map<number, IBase[]>();
  schedules.forEach((schedule) =>
    schedule.availabilities.forEach((availability) => {
      const tripIds = loadedTripIds.get(availability.id) ?? new Set();
      availability.trips.forEach((trip) => tripIds.add(trip.id));
      loadedTripIds.set(availability.id, tripIds);
      loadedRows.set(availability.id, [...(loadedRows.get(availability.id) ?? []), { id: schedule.id, name: schedule.name }]);
    }),
  );
  const incomingPlaced = new Set<number>();

  schedules.forEach((schedule) => {
    const currentRow: IBase = { id: schedule.id, name: schedule.name };
    schedule.availabilities.forEach((availability) => {
      const edited = changes.get(availability.id)?.availability;
      if (!edited) {
        place(currentRow, availability);
        return;
      }
      const availabilityRow = movedTo(availability[field], edited[field]) ?? currentRow;
      const fragments = new Map([[availabilityRow.id, { row: availabilityRow, trips: [] as IVehicleScheduleTrip[] }]]);
      const add = (row: IBase, trip: IVehicleScheduleTrip) => {
        const fragment = fragments.get(row.id) ?? { row, trips: [] };
        fragment.trips.push(trip);
        fragments.set(row.id, fragment);
      };
      availability.trips.forEach((trip) => {
        const editedTrip = edited.trips.find((item) => item.id === trip.id);
        if (editedTrip) {
          add(movedTo(trip[field], editedTrip[field]) ?? currentRow, editedTrip);
        }
      });
      if (!incomingPlaced.has(availability.id)) {
        incomingPlaced.add(availability.id);
        edited.trips
          .filter((trip) => !loadedTripIds.get(availability.id)!.has(trip.id))
          .forEach((trip) => add(loadedRows.get(availability.id)!.find((row) => row.id === trip[field]?.id) ?? availabilityRow, trip));
      }
      fragments.forEach(({ row, trips }) => place(row, { ...edited, trips }));
    });
  });
  changes.forEach(({ availability }) => {
    const row = availability[field];
    if (!loadedTripIds.has(availability.id) && row?.id !== undefined) {
      place(row, availability);
    }
  });
  return [...rows.values()];
}
